'use client';

import { useLocale } from '@/lib/i18n/context';
import { buildRouteSchema } from '@/lib/route-schema';

/**
 * Structured data for an index page whose own `page.tsx` is a client
 * component.
 *
 * `RouteSchema` normally lives in a route's `layout.tsx`, which is a server
 * component. That works for leaf routes, but a layout also wraps every child
 * segment — so putting `/channels`'s node in `channels/layout.tsx` made
 * `/channels/voice` emit a `CollectionPage` describing `/channels`, a page it
 * is not. Declaring a node for a different URL is exactly the kind of claim
 * the rest of this work exists to remove.
 *
 * Rendering from the page itself scopes it correctly. The page is a client
 * component, but Next server-renders those too, so the script is present in
 * the initial HTML where a crawler can see it — verified, not assumed.
 */
export function IndexRouteSchema({
  route,
  override,
}: {
  route: string;
  override?: { name: string; description: string };
}) {
  const locale = useLocale();
  const schema = buildRouteSchema(route, locale, override);
  if (!schema) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
